import React, { useContext, useRef, useMemo, useState } from 'react';

import { MDText } from 'i18n-react';
import PropTypes from 'prop-types';

import { getOrCreateGlobalContext, CONTEXT_NAMES } from '../globalContexts';
import { defaultLocale, supportedLocales } from './consts';
import { createTranslationLoader } from './TLoader';
import type { TranslationFileLoader } from './types';

type LocaleContextType = {
  locale?: string;
  setLocale?: (locale: string) => void;
};
const LocaleContext = getOrCreateGlobalContext<LocaleContextType>(
  CONTEXT_NAMES.LocaleContext
);

type UseLocaleFns = LocaleContextType & {
  T: MDText;
};

/**
 * @name useCustomLocale - this function allows you to load a custom locale regardless of the provider.
 * @param {function} translationFileLoader - function to load translation file.
 * @param {string} locale [defaultLocale]
 * @param {function} setLocale
 */
const useCustomLocale = (
  translationFileLoader: TranslationFileLoader,
  locale: string = defaultLocale,
  setLocale?: (locale: string) => void
): UseLocaleFns => {
  const translationFileLoaderRef = useRef(translationFileLoader);
  const T = useMemo(() => {
    const translations = createTranslationLoader({
      translationFileLoader: translationFileLoaderRef.current,
    })(locale);
    return new MDText(translations || {});
  }, [locale]);

  return { T, locale, setLocale };
};

/**
 * @name useLocale - this function allows you to load a locale using the locale from the provider.
 * @param {function} translationFileLoader - function to load translation file.
 */
export const useLocale = (
  translationFileLoader: TranslationFileLoader
): UseLocaleFns => {
  const contextValue = useContext(LocaleContext);
  return useCustomLocale(
    translationFileLoader,
    contextValue?.locale,
    contextValue?.setLocale
  );
};

export const LocaleProvider: React.FC<{
  children: unknown;
  locale?: string;
}> = ({ children, locale }) => {
  const browserLanguage = supportedLocales.find(
    (l) => l === navigator.language
  );

  const [customLocale, setCustomLocale] = useState(
    browserLanguage || defaultLocale
  );

  return (
    <LocaleContext.Provider
      value={{
        locale: locale || customLocale,
        setLocale: setCustomLocale,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

LocaleProvider.propTypes = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
};

export { defaultLocale, supportedLocales };
